// src/pages/KarigarManagement/Payment/KarigarEarningsPage.jsx
// Fixed: Proper payments fetching for specific karigar using getPaymentsByKarigar

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePaymentManagement, useKarigarManagement } from "@/hooks/useKarigar";
import { Button } from "@/components/ui-v2/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui-v2/card";
import { Badge } from "@/components/ui-v2/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui-v2/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui-v2/select";
import {
  ArrowLeft,
  IndianRupee,
  TrendingUp,
  TrendingDown,
  Calendar,
  Briefcase,
  Download,
  RefreshCw,
} from "lucide-react";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { toast } from "sonner";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";

// Status colors
const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-blue-100 text-blue-800",
  processing: "bg-purple-100 text-purple-800",
  processed: "bg-green-100 text-green-800",
  paid: "bg-green-200 text-green-900",
  completed: "bg-green-200 text-green-900",
  rejected: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-800",
};

const KarigarEarningsPage = () => {
  const navigate = useNavigate();
  const { karigarId } = useParams();

  const {
    currentKarigar,
    loading: karigarLoading,
    getKarigarById,
  } = useKarigarManagement();

  const {
    earnings,
    karigarEarnings, // ✅ Use the correct state
    loading: paymentLoading,
    error,
    getEarnings,
    getPaymentsByKarigar, // ✅ Use karigar-specific function
    exportPayments,
    clearError,
    clearEarnings,
  } = usePaymentManagement();

  // Local state for karigar payments
  const [karigarPayments, setKarigarPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);

  const [selectedPeriod, setSelectedPeriod] = useState("this_month");
  const [dateRange, setDateRange] = useState({
    startDate: format(startOfMonth(new Date()), "yyyy-MM-dd"),
    endDate: format(endOfMonth(new Date()), "yyyy-MM-dd"),
  });

  // Fetch karigar details
  useEffect(() => {
    if (karigarId) {
      getKarigarById(karigarId);
    }
    return () => {
      clearEarnings();
    };
  }, [karigarId]);

  // Fetch earnings and payments when period changes
  useEffect(() => {
    if (karigarId) {
      fetchEarningsData();
    }
  }, [karigarId, dateRange]);

  // Handle period change
  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    const now = new Date();
    let startDate, endDate;

    switch (period) {
      case "this_month":
        startDate = startOfMonth(now);
        endDate = endOfMonth(now);
        break;
      case "last_month":
        startDate = startOfMonth(subMonths(now, 1));
        endDate = endOfMonth(subMonths(now, 1));
        break;
      case "last_3_months":
        startDate = startOfMonth(subMonths(now, 2));
        endDate = endOfMonth(now);
        break;
      case "last_6_months":
        startDate = startOfMonth(subMonths(now, 5));
        endDate = endOfMonth(now);
        break;
      case "this_year":
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31);
        break;
      default:
        startDate = startOfMonth(now);
        endDate = endOfMonth(now);
    }

    setDateRange({
      startDate: format(startDate, "yyyy-MM-dd"),
      endDate: format(endDate, "yyyy-MM-dd"),
    });
  };

  // Fetch earnings data
  const fetchEarningsData = async () => {
    // Fetch earnings summary
    getEarnings(karigarId, {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });

    // Fetch payments for this karigar
    setLoadingPayments(true);
    try {
      const result = await getPaymentsByKarigar(karigarId, {
        dateFrom: dateRange.startDate,
        dateTo: dateRange.endDate,
        limit: 100,
      });
      // If the thunk returns the payments data, use it
      if (result?.payload?.payments) {
        setKarigarPayments(result.payload.payments);
      } else if (Array.isArray(result?.payload)) {
        setKarigarPayments(result.payload);
      }
    } catch (err) {
      console.error("Error fetching payments:", err);
    } finally {
      setLoadingPayments(false);
    }
  };

  // Handle error
  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error]);

  // Handle export
  const handleExport = async () => {
    try {
      await exportPayments({
        karigarId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        format: "xlsx",
      });
      toast.success("Export started");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Get status color
  const getStatusColor = (status) => {
    return STATUS_COLORS[status?.toLowerCase()] || "bg-gray-100 text-gray-800";
  };

  // Use earnings from hook or karigarEarnings
  const earningsData = earnings || karigarEarnings;

  const loading =
    karigarLoading.detail || paymentLoading.earnings || loadingPayments;

  if (karigarLoading.detail) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Karigar Earnings</h1>
            <p className="text-muted-foreground">
              {currentKarigar?.name} - {currentKarigar?.karigarCode}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="last_3_months">Last 3 Months</SelectItem>
              <SelectItem value="last_6_months">Last 6 Months</SelectItem>
              <SelectItem value="this_year">This Year</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={() => fetchEarningsData()}>
            <RefreshCw
              className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>

          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Karigar Info Card */}
      {currentKarigar && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-2xl font-bold">
                  {currentKarigar.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div>
                <h2 className="text-xl font-semibold">{currentKarigar.name}</h2>
                <p className="text-muted-foreground">
                  {currentKarigar.karigarCode}
                </p>
                <Badge className="mt-1">{currentKarigar.status}</Badge>
              </div>
              <div className="ml-auto text-right">
                <p className="text-sm text-muted-foreground">Contact</p>
                <p className="font-medium">{currentKarigar.phone || "-"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Earnings Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <IndianRupee className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Earnings</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(
                    earningsData?.totalEarnings || earningsData?.total || 0
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <IndianRupee className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Paid Amount</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(
                    earningsData?.paidAmount || earningsData?.paid || 0
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <IndianRupee className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Amount</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(
                    earningsData?.pendingAmount || earningsData?.pending || 0
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Briefcase className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Jobs Completed</p>
                <p className="text-2xl font-bold">
                  {earningsData?.jobsCompleted ||
                    earningsData?.completedJobs ||
                    karigarPayments?.length ||
                    0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Earnings Breakdown */}
      {earningsData?.breakdown && earningsData.breakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Earnings Breakdown</CardTitle>
            <CardDescription>By payment type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {earningsData.breakdown.map((item, idx) => (
                <div key={idx} className="p-4 bg-muted/50 rounded">
                  <p className="text-sm text-muted-foreground">
                    {item.type?.replace(/_/g, " ") ||
                      item._id?.replace(/_/g, " ")}
                  </p>
                  <p className="text-xl font-bold">
                    {formatCurrency(item.amount || item.total)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {item.count} payment{item.count !== 1 ? "s" : ""}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Trend */}
      {earningsData?.monthlyTrend && earningsData.monthlyTrend.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Monthly Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
              {earningsData.monthlyTrend.map((month, idx) => (
                <div key={idx} className="text-center p-3 bg-muted/50 rounded">
                  <p className="text-sm text-muted-foreground">
                    {month.month || month._id}
                  </p>
                  <p className="text-lg font-bold">
                    {formatCurrency(month.amount || month.total)}
                  </p>
                  {month.change !== undefined && (
                    <div
                      className={`flex items-center justify-center gap-1 text-xs ${
                        month.change >= 0 ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {month.change >= 0 ? (
                        <TrendingUp className="h-3 w-3" />
                      ) : (
                        <TrendingDown className="h-3 w-3" />
                      )}
                      {Math.abs(month.change)}%
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment History */}
      <Card>
        <CardHeader>
          <CardTitle>Payment History</CardTitle>
          <CardDescription>
            {format(new Date(dateRange.startDate), "dd MMM yyyy")} -{" "}
            {format(new Date(dateRange.endDate), "dd MMM yyyy")}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loadingPayments ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : karigarPayments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No payments in this period
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payment ID</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {karigarPayments.map((payment) => (
                  <TableRow
                    key={payment._id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() =>
                      navigate(`/karigar-management/payments/${payment._id}`)
                    }
                  >
                    <TableCell className="font-medium">
                      {payment.paymentNumber || payment._id?.slice(-8)}
                    </TableCell>
                    <TableCell>
                      {payment.job?.jobNumber ||
                        payment.jobId?.jobNumber ||
                        "-"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {payment.paymentType?.replace(/_/g, " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-semibold">
                      {formatCurrency(payment.amount)}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(payment.status)}>
                        {payment.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {payment.createdAt
                        ? format(new Date(payment.createdAt), "dd MMM yyyy")
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Deductions Summary */}
      {earningsData?.deductions &&
        (earningsData.deductions.total > 0 ||
          earningsData.deductions.items?.length > 0) && (
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="text-red-600">Deductions</CardTitle>
              <CardDescription>
                Total deducted: {formatCurrency(earningsData.deductions.total)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {earningsData.deductions.items?.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex justify-between p-2 bg-red-50 rounded"
                  >
                    <span>{item.reason || item.type}</span>
                    <span className="font-medium text-red-600">
                      -{formatCurrency(item.amount)}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
    </div>
  );
};

export default KarigarEarningsPage;
